import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  DollarSign,
  TicketCheck,
  RotateCcw,
  PiggyBank,
  Loader2,
  BarChart3,
  RefreshCw,
  ArrowRight,
  Receipt,
  Download,
} from "lucide-react";
import {
  organiserDashboardApi,
  organiserSalesCsvUrl,
  centsToAUD,
  formatAuDate,
} from "../../lib/events";
import { extractError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const RANGES = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "all", label: "All time" },
];

function StatCard({ icon: Icon, label, value, sub, testid, tone = "default" }) {
  const toneCls =
    tone === "accent"
      ? "text-[var(--gt-accent)]"
      : "text-[var(--gt-ink)]";
  return (
    <div className="gt-card" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <Icon size={18} className="text-[var(--gt-ink-2)]" />
      </div>
      <div className="gt-label mb-1">{label}</div>
      <div
        className={`font-heading text-2xl sm:text-3xl font-black tracking-tighter leading-none ${toneCls}`}
        data-testid={`${testid}-value`}
      >
        {value}
      </div>
      {sub && (
        <div className="mt-1 text-xs text-[var(--gt-ink-3)]" data-testid={`${testid}-sub`}>
          {sub}
        </div>
      )}
    </div>
  );
}

export default function SalesDashboard() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const d = await organiserDashboardApi.salesSummary(range);
        setData(d);
        setError("");
      } catch (e) {
        setError(extractError(e));
      } finally {
        setLoading(false);
      }
    },
    [range],
  );

  useEffect(() => {
    load();
  }, [load]);

  const agg = data?.aggregate;
  const isEmpty = useMemo(
    () => !!data && (!agg || agg.orders_count === 0),
    [data, agg],
  );

  return (
    <section className="p-6 sm:p-10" data-testid="sales-dashboard-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <div className="gt-label mb-2">Revenue</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
              Sales
            </h2>
            <p className="text-[var(--gt-ink-2)] text-sm mt-2">
              All figures in AUD.{isAdmin ? " Service charge and tax are itemised on each order." : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="sales-range-group">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                className={`px-3 py-2 text-xs uppercase tracking-widest border rounded-sm transition-colors ${
                  range === r.key
                    ? "bg-[var(--gt-ink)] text-white border-[var(--gt-ink)]"
                    : "border-[var(--gt-line)] text-[var(--gt-ink-2)] hover:border-[var(--gt-ink)]"
                }`}
                data-testid={`sales-range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => load(true)}
              className="gt-btn-ghost text-sm"
              data-testid="sales-refresh-btn"
              aria-label="Refresh sales"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <a
              href={organiserSalesCsvUrl(range)}
              className="gt-btn-primary text-sm py-2"
              data-testid="sales-export-csv-btn"
              aria-label="Export sales CSV for selected range"
            >
              <Download size={14} /> Export CSV
            </a>
          </div>
        </div>

        {loading && !data && (
          <div
            className="flex items-center gap-3 text-[var(--gt-ink-2)]"
            data-testid="sales-loading"
          >
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Loading your sales…</span>
          </div>
        )}

        {error && (
          <div
            className="gt-card border-[var(--gt-accent)] text-[var(--gt-accent)]"
            data-testid="sales-error"
          >
            {error}
          </div>
        )}

        {data && !error && (
          <>
            {isEmpty ? (
              <div
                className="gt-card-hard text-center p-10"
                data-testid="sales-empty"
              >
                <BarChart3
                  size={32}
                  className="mx-auto text-[var(--gt-ink-3)] mb-3"
                  strokeWidth={1.5}
                />
                <div className="font-heading text-xl font-bold mb-2">
                  No sales yet for this range.
                </div>
                <p className="text-sm text-[var(--gt-ink-2)] max-w-md mx-auto">
                  Once buyers start purchasing your tickets, revenue and GST-inclusive
                  breakdowns will appear here in AUD.
                </p>
                <Link
                  to="/dashboard/events"
                  className="gt-btn-primary mt-6 inline-flex"
                  data-testid="sales-empty-cta"
                >
                  Go to my events <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    icon={DollarSign}
                    label="Gross sales (AUD)"
                    value={centsToAUD(agg.gross_sales_cents || 0)}
                    sub={`Tickets ${centsToAUD(agg.gross_ticket_cents || 0)} · Extras ${centsToAUD(
                      agg.addons_total_cents || 0,
                    )}`}
                    testid="stat-gross-sales"
                  />
                  <StatCard
                    icon={RotateCcw}
                    label="Refunds (AUD)"
                    value={centsToAUD(agg.refunded_total_cents || 0)}
                    sub={
                      agg.refunded_total_cents
                        ? "Refunds reduce net sales"
                        : "No refunds"
                    }
                    testid="stat-refunds"
                    tone={agg.refunded_total_cents ? "accent" : "default"}
                  />
                  <StatCard
                    icon={PiggyBank}
                    label="Net sales (AUD)"
                    value={centsToAUD(agg.net_sales_cents || 0)}
                    sub="Gross minus refunds · GST-inclusive"
                    testid="stat-net-sales"
                  />
                  <StatCard
                    icon={TicketCheck}
                    label="Tickets sold"
                    value={agg.tickets_count || 0}
                    sub={`${agg.orders_count || 0} orders · Avg ${centsToAUD(
                      agg.avg_order_cents || 0,
                    )}`}
                    testid="stat-tickets-sold"
                  />
                </div>

                <div className="gt-card mt-8" data-testid="sales-gst-breakdown">
                  <div className="gt-label mb-3">{isAdmin ? "GST & service charges (AUD)" : "Revenue summary (AUD)"}</div>
                  <div className={`grid grid-cols-2 gap-4 text-sm ${isAdmin ? "sm:grid-cols-4" : "sm:grid-cols-2"}`}>
                    <div>
                      <div className="text-[var(--gt-ink-3)] text-xs uppercase tracking-widest mb-1">
                        Amount paid by buyers
                      </div>
                      <div
                        className="font-mono font-bold"
                        data-testid="sales-amount-total"
                      >
                        {centsToAUD(agg.amount_total_cents || 0)}
                      </div>
                    </div>
                    {isAdmin && (
                      <div>
                        <div className="text-[var(--gt-ink-3)] text-xs uppercase tracking-widest mb-1">
                          GST
                        </div>
                        <div className="font-mono font-bold" data-testid="sales-gst">
                          {centsToAUD(agg.gst_cents || 0)}
                        </div>
                      </div>
                    )}
                    {isAdmin && (
                      <div>
                        <div className="text-[var(--gt-ink-3)] text-xs uppercase tracking-widest mb-1">
                          Service charge
                        </div>
                        <div className="font-mono font-bold" data-testid="sales-fees">
                          {centsToAUD(agg.fee_cents || 0)}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-[var(--gt-ink-3)] text-xs uppercase tracking-widest mb-1">
                        Donations
                      </div>
                      <div
                        className="font-mono font-bold"
                        data-testid="sales-donations"
                      >
                        {centsToAUD(agg.donation_cents || 0)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Per-event breakdown */}
                <div
                  className="gt-card mt-8"
                  data-testid="sales-per-event"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-xl font-black tracking-tight">
                      Sales by event
                    </h3>
                    <span className="gt-label">
                      {data.per_event.filter((e) => e.totals.orders_count > 0).length}{" "}
                      selling · {data.events_count} total
                    </span>
                  </div>
                  {data.per_event.filter((e) => e.totals.orders_count > 0).length === 0 ? (
                    <div
                      className="text-sm text-[var(--gt-ink-2)]"
                      data-testid="sales-per-event-empty"
                    >
                      No event has sales in this range yet.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left border-b border-[var(--gt-line)]">
                            <th className="py-2 pr-3 gt-label">Event</th>
                            <th className="py-2 px-3 gt-label text-right">Tickets</th>
                            <th className="py-2 px-3 gt-label text-right">Gross</th>
                            <th className="py-2 px-3 gt-label text-right">Refunds</th>
                            <th className="py-2 px-3 gt-label text-right">Net</th>
                            <th className="py-2 pl-3 gt-label text-right">Report</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.per_event
                            .filter((e) => e.totals.orders_count > 0)
                            .map((e) => (
                              <tr
                                key={e.id}
                                className="border-b border-[var(--gt-line)]/60 hover:bg-[var(--gt-bg-surface)]"
                                data-testid={`sales-event-row-${e.id}`}
                              >
                                <td className="py-3 pr-3">
                                  <div className="font-semibold text-[var(--gt-ink)] truncate max-w-[240px]">
                                    {e.title}
                                  </div>
                                  <div className="text-xs text-[var(--gt-ink-3)]">
                                    {formatAuDate(e.start_at)}
                                  </div>
                                </td>
                                <td className="py-3 px-3 text-right font-mono">
                                  {e.totals.tickets_count}
                                </td>
                                <td className="py-3 px-3 text-right font-mono">
                                  {centsToAUD(e.totals.gross_sales_cents)}
                                </td>
                                <td className="py-3 px-3 text-right font-mono text-[var(--gt-accent)]">
                                  {e.totals.refunded_total_cents
                                    ? `−${centsToAUD(e.totals.refunded_total_cents)}`
                                    : "—"}
                                </td>
                                <td className="py-3 px-3 text-right font-mono font-bold">
                                  {centsToAUD(e.totals.net_sales_cents)}
                                </td>
                                <td className="py-3 pl-3 text-right">
                                  <Link
                                    to={`/dashboard/events/${e.id}/reports`}
                                    className="text-xs inline-flex items-center gap-1 text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)]"
                                    data-testid={`sales-event-report-${e.id}`}
                                  >
                                    <Receipt size={12} /> Open
                                  </Link>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
